package fr.manyverse.app.presentation.components.modules.announcement

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.Announcement
import androidx.compose.material.icons.filled.Cancel
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.unit.dp
import coil3.compose.AsyncImage
import fr.manyverse.app.domain.model.ActiveAnnouncement
import fr.manyverse.app.domain.model.Character
import kotlin.time.Instant
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.format
import kotlinx.datetime.format.char
import kotlinx.datetime.toLocalDateTime
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.announcement_active_announcements
import manyverse.shared.generated.resources.announcement_cancel
import manyverse.shared.generated.resources.announcement_in_progress
import manyverse.shared.generated.resources.announcement_launched_at
import org.jetbrains.compose.resources.stringResource

@Composable
fun AnnouncementGmSection(
    activeAnnouncements: List<ActiveAnnouncement>,
    characters: List<Character>,
    onCancel: (announcementEventId: String) -> Unit,
    modifier: Modifier = Modifier
) {
    if (activeAnnouncements.isEmpty()) return

    Column(
        modifier = modifier
            .fillMaxWidth()
            .padding(top = 8.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        Text(
            text = stringResource(Res.string.announcement_active_announcements),
            style = MaterialTheme.typography.titleSmall,
            color = MaterialTheme.colorScheme.primary
        )

        activeAnnouncements.forEach { announcement ->
            val targetNames = announcement.targetParticipantIds.joinToString(", ") { userId ->
                characters.find { it.userId == userId }?.name ?: userId.take(8)
            }
            val launchedAt = stringResource(
                Res.string.announcement_launched_at,
                formatTime(announcement.createdAt)
            )
            val fullText = announcement.text?.takeIf { it.isNotBlank() }

            Card(
                modifier = Modifier.fillMaxWidth(),
                colors = CardDefaults.cardColors(
                    containerColor = MaterialTheme.colorScheme.secondaryContainer
                ),
                elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp, vertical = 8.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    if (announcement.imageUrl != null) {
                        AsyncImage(
                            model = announcement.imageUrl,
                            contentDescription = null,
                            contentScale = ContentScale.Crop,
                            modifier = Modifier
                                .size(48.dp)
                                .clip(RoundedCornerShape(4.dp))
                        )
                    } else {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.Announcement,
                            contentDescription = null,
                            tint = MaterialTheme.colorScheme.onSecondaryContainer,
                            modifier = Modifier.size(24.dp)
                        )
                    }
                    Column(modifier = Modifier.weight(1f)) {
                        Text(
                            text = "${stringResource(Res.string.announcement_in_progress)} $launchedAt",
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSecondaryContainer
                        )
                        if (targetNames.isNotBlank()) {
                            Text(
                                text = targetNames,
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.onSecondaryContainer.copy(alpha = 0.7f)
                            )
                        }
                        if (fullText != null) {
                            val preview = fullText.take(30)
                            Text(
                                text = if (fullText.length > 30) "$preview…" else preview,
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.onSecondaryContainer.copy(alpha = 0.7f)
                            )
                        }
                    }
                    IconButton(onClick = { onCancel(announcement.eventId) }) {
                        Icon(
                            imageVector = Icons.Default.Cancel,
                            contentDescription = stringResource(Res.string.announcement_cancel),
                            tint = MaterialTheme.colorScheme.onSecondaryContainer
                        )
                    }
                }
            }
        }
    }
}

private fun formatTime(instant: Instant): String {
    val dt = instant.toLocalDateTime(TimeZone.currentSystemDefault())
    return dt.format(LocalDateTime.Format { hour(); char(':'); minute() })
}
